import { R, type Ramp, type Toy } from "./toy.ts";
import {
  INK, WHITE, arc, blade, brow, crown, eyeBig, eyeDot, eyeGlow, eyeSleepy, frown, grin, hoop,
  nose, paws, rivet, smile, teeth, visor,
} from "./parts.ts";
import type { Paint } from "./frontier.ts";

/**
 * Undernet bestiary: Neon Grid, the Mempool Caverns, Coral Ledger and Cloud Chain, on the same
 * vinyl-toy rig and 32×32 design grid as creatures.ts (feet on row 29, key light upper left).
 * Original IP only.
 */

// ------------------------------------------------------------------------------------------------
// Small parts
// ------------------------------------------------------------------------------------------------

/** Dark screen face with two glowing pixel eyes. */
function screen(t: Toy, x: number, y: number, w: number, h: number, glow: Ramp = R.aqua, mood: "calm" | "smug" | "angry" = "calm"): void {
  t.slab(x + w / 2, y + h / 2, w / 2, h / 2, 1.2, R.ink, { flat: 1 });
  t.hline(x + 1, y, w - 2, R.slate, 1);
  const ey = y + Math.floor(h / 2) - 1;
  const ex = [x + Math.round(w * 0.28), x + Math.round(w * 0.72) - 1];
  for (const e of ex) {
    if (mood === "smug") t.hline(e - 1, ey + 1, 3, glow, 1);
    else t.rect(e - 1, ey, 2, 2, glow, 1);
    t.px(e - 1, ey, glow, 0);
  }
  if (mood === "angry") {
    t.px(ex[0]! - 2, ey - 1, glow, 2);
    t.px(ex[1]! + 1, ey - 1, glow, 2);
  }
}

/** Three typing dots. */
function typing(t: Toy, x: number, y: number, ramp: Ramp = R.aqua): void {
  for (let i = 0; i < 3; i++) t.px(x + i * 2, y, ramp, i === 1 ? 0 : 1);
}

/** Small antenna with a lit bulb. */
function antenna(t: Toy, x: number, y0: number, h: number, bulb: Ramp = R.neon): void {
  t.vline(x, y0 - h, h, R.chrome, 2);
  t.ell(x, y0 - h - 1, 1.4, 1.4, bulb, { spec: false, rim: false });
  t.px(x, y0 - h - 2, WHITE);
}

/** Pixel shard offset: a copied slice of what is already there, shifted sideways, in a glitch tint. */
function glitchBar(t: Toy, x: number, y: number, w: number, ramp: Ramp): void {
  t.hline(x, y, w, ramp, 1);
  t.px(x + w, y, ramp, 0);
  t.px(x - 1, y + 1, ramp, 2);
}

/** Circuit trace: an L of gold with a pad at the end. */
function trace(t: Toy, x0: number, y0: number, x1: number, y1: number): void {
  t.hline(Math.min(x0, x1), y0, Math.abs(x1 - x0) + 1, R.gold, 2);
  t.vline(x1, Math.min(y0, y1), Math.abs(y1 - y0) + 1, R.gold, 2);
  t.px(x1, y1, R.gold, 0);
}

/** A crescent moon: a lit disc with a bite taken out of the right. */
function crescent(t: Toy, cx: number, cy: number, r: number, ramp: Ramp = R.moon, bite: Ramp = R.night): void {
  t.ell(cx, cy, r, r, ramp, { spec: false, rim: false, occ: false });
  t.ell(cx + r * 0.55, cy - r * 0.2, r * 0.8, r * 0.8, bite, { flat: 2, occ: false });
}

/** Four-point star sparkle. */
function star(t: Toy, x: number, y: number, ramp: Ramp = R.moon): void {
  t.px(x, y, WHITE);
  t.px(x - 1, y, ramp, 1); t.px(x + 1, y, ramp, 1);
  t.px(x, y - 1, ramp, 1); t.px(x, y + 1, ramp, 1);
}

/** Puffy cloud mass made of overlapping balls. */
function cloudPuff(t: Toy, cx: number, cy: number, w: number, h: number, ramp: Ramp = R.cloud): void {
  const n = Math.max(3, Math.round(w / 3));
  for (let i = 0; i < n; i++) {
    const u = n === 1 ? 0 : i / (n - 1);
    const x = cx - w / 2 + u * w;
    const bump = Math.sin(u * Math.PI) * h * 0.45;
    t.ell(x, cy - bump * 0.5, h * 0.5 + bump * 0.35, h * 0.5 + bump * 0.2, ramp, { spec: false });
  }
}

/** Glowing mushroom cap with spots. */
function shroomCap(t: Toy, cx: number, cy: number, rx: number, ry: number, cap: Ramp, spot: Ramp): void {
  t.ell(cx, cy, rx, ry, cap, { dome: 0.7 });
  t.hline(Math.round(cx - rx + 1), Math.round(cy + ry - 1), Math.round(rx * 2 - 1), cap, 3);
  const spots: [number, number][] = [[-0.5, -0.3], [0.35, -0.45], [0.1, 0.1], [-0.15, -0.65], [0.62, 0.05]];
  for (const [sx, sy] of spots) {
    const x = Math.round(cx + sx * rx), y = Math.round(cy + sy * ry);
    t.ell(x, y, Math.max(1, rx * 0.14), Math.max(1, ry * 0.2), spot, { spec: false, rim: false, occ: false });
  }
}

/** Shrimp / crab eye on a stalk. */
function stalkEye(t: Toy, x0: number, y0: number, x1: number, y1: number, ramp: Ramp): void {
  t.cap(x0, y0, x1, y1, 0.8, ramp, { shade: 1, spec: false, occ: false });
  t.ell(x1, y1 - 1, 1.8, 1.8, R.paper, { spec: false, occ: false });
  t.rect(Math.round(x1) - 1, Math.round(y1) - 2, 2, 2, INK, 1);
  t.px(Math.round(x1) - 1, Math.round(y1) - 2, WHITE);
}

/** Crab claw: arm plus an open pincer. */
function claw(t: Toy, sx: number, sy: number, ex: number, ey: number, r: number, ramp: Ramp): void {
  t.cap(sx, sy, ex, ey + r * 0.6, r * 0.5, ramp, { shade: 1 });
  t.ell(ex, ey, r, r * 0.85, ramp);
  const s = ex > sx ? 1 : -1;
  t.poly([[ex - s * 0.5, ey - r * 0.5], [ex + s * r * 1.1, ey - r * 1.5], [ex + s * r * 0.8, ey - r * 0.2]], ramp, { tone: 1 });
  t.px(Math.round(ex + s * r * 0.3), Math.round(ey - r * 0.3), INK, 1);
}

/** Jellyfish tentacle: a wavy dotted strand. */
function tentacle(t: Toy, x: number, y0: number, len: number, phase: number, ramp: Ramp): void {
  for (let i = 0; i < len; i++) {
    const dx = Math.round(Math.sin(i * 0.9 + phase) * 1.2);
    t.px(x + dx, y0 + i, ramp, i % 3 === 2 ? 2 : 1);
  }
}

/** Rocket fin. */
function fin(t: Toy, x: number, y: number, dir: 1 | -1, ramp: Ramp): void {
  t.poly([[x, y - 5], [x + dir * 4, y + 1], [x + dir * 4, y + 3], [x, y + 2]], ramp, { grad: true });
}

// ------------------------------------------------------------------------------------------------
// Neon Grid (AI)
// ------------------------------------------------------------------------------------------------

const chatbit: Paint = (t, back) => {
  // Hover jet under the bubble.
  t.ell(16, 28.5, 4, 1, R.aqua, { flat: 1, occ: false });
  t.ell(16, 27, 2.4, 1.6, R.chrome, { shade: 1, spec: false });
  // The speech-bubble body with its tail.
  t.poly([[8, 21], [12, 22], [7, 26]], R.steel, { grad: true });
  t.slab(16, 15, 10.5, 8, 5, R.steel);
  antenna(t, 16, 7, 3, R.aqua);
  if (back) {
    for (let y = 12; y <= 18; y += 2) t.hline(11, y, 10, R.chrome, 2);
    return;
  }
  screen(t, 9, 10, 14, 9, R.aqua);
  typing(t, 14, 17, R.aqua);
  t.px(7, 14, R.neon, 1);
  t.px(25, 14, R.neon, 1);
};

const chatlord: Paint = (t, back) => {
  // Orbiting reply bubbles.
  for (const [x, y, r] of [[4, 8, 3], [28, 7, 3.2], [3, 19, 2.4], [29, 20, 2.6]] as const) {
    t.slab(x, y, r, r * 0.75, r * 0.6, back ? R.steel : R.lime, { spec: false });
    if (!back) typing(t, Math.round(x) - 2, Math.round(y), R.green);
  }
  // Arms.
  t.cap(8, 18, 4, 24, 1.8, R.chrome, { shade: 1 });
  t.cap(24, 18, 28, 24, 1.8, R.chrome, { shade: 1 });
  // Stacked bubble body on a hover ring.
  t.ell(16, 28.3, 6, 1.1, R.lime, { flat: 1, occ: false });
  t.slab(16, 23, 8, 5, 3, R.steel, { shade: 1 });
  t.slab(16, 13, 11, 8.5, 5, R.steel);
  for (const x of [11, 16, 21]) antenna(t, x, 5, x === 16 ? 4 : 2, R.lime);
  if (back) {
    for (let y = 10; y <= 17; y += 2) t.hline(10, y, 12, R.chrome, 2);
    t.hline(12, 22, 8, R.chrome, 2);
    return;
  }
  screen(t, 8, 8, 16, 10, R.lime, "smug");
  t.hline(13, 16, 6, R.lime, 1);
  t.rect(13, 21, 6, 4, R.ink, 1);
  typing(t, 14, 23, R.lime);
};

const glitchpup: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Tail, drawn a frame late.
  t.cap(m(22), 21, m(27), 15, 1.8, R.steel, { shade: 1 });
  glitchBar(t, m(26) - 1, 14, 3, R.neon);
  t.ell(15, 22, 8, 6, R.steel);
  paws(t, 15, 4.5, 2.6, R.chrome, { shade: -1 });
  t.ell(15, 12, 7.5, 6.5, R.steel);
  // Ears, one of them displaced by a scan line.
  t.poly([[9, 9], [7, 1], [12, 5]], R.chrome, { grad: true });
  t.poly([[23, 7], [25, -0.5], [19, 3.5]], R.chrome, { grad: true });
  glitchBar(t, 20, 3, 4, R.aqua);
  // Displaced slices across the body.
  glitchBar(t, 6, 20, 5, R.neon);
  glitchBar(t, 19, 24, 5, R.aqua);
  if (back) return;
  t.ell(15, 15, 4.2, 2.8, R.paper, { dome: 0.5, rim: false, spec: false });
  nose(t, 14, 14);
  smile(t, 13, 16, 4);
  eyeBig(t, 9, 9, R.aqua);
  // The other eye is a stuck pixel cross.
  t.px(18, 10, R.neon, 1); t.px(20, 10, R.neon, 1); t.px(19, 11, R.neon, 1); t.px(18, 12, R.neon, 1); t.px(20, 12, R.neon, 1);
};

const glitchhound: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(m(21), 19, m(28), 11, 2.2, R.chrome);
  for (const y of [10, 13]) glitchBar(t, m(27) - 2, y, 4, R.neon);
  t.ell(15, 20, 10, 7.5, R.chrome);
  for (const x of [8, 22]) {
    t.cap(x, 22, x, 27.5, 2.6, R.chrome, { shade: 1, spec: false });
    t.ell(x, 28.2, 2.8, 1.3, R.ink, { spec: false, flat: 1 });
  }
  // Spiked collar with a status LED.
  t.hline(8, 15, 15, R.ink, 1);
  for (const x of [9, 13, 17, 21]) t.poly([[x, 15], [x + 2, 15], [x + 1, 17.5]], R.steel, { tone: 0 });
  t.ell(15, 10, 8, 6.5, R.chrome);
  t.poly([[8, 8], [5, -0.5], [12, 4]], R.chrome, { grad: true });
  t.poly([[22, 8], [25, -0.5], [18, 4]], R.chrome, { grad: true });
  // Neon scan stripes.
  for (const [x, y, w] of [[5, 18, 6], [20, 22, 7], [9, 25, 5], [18, 6, 5]] as const) glitchBar(t, x, y, w, y % 2 ? R.aqua : R.neon);
  if (back) return;
  t.ell(15, 13.5, 5, 3.2, R.slate, { dome: 0.5, rim: false, spec: false });
  nose(t, 14, 12);
  teeth(t, 11, 15, 8);
  eyeGlow(t, 8, 7, R.neon);
  eyeGlow(t, 18, 7, R.aqua);
  brow(t, 8, 7, 1);
  brow(t, 18, 7, -1);
};

/** Fan disc: frame, spinning blades and a hub; returns nothing. */
function fanDisc(t: Toy, cx: number, cy: number, r: number, blades: Ramp, frame: Ramp = R.chrome): void {
  t.ell(cx, cy, r, r, frame);
  t.ell(cx, cy, r - 1.4, r - 1.4, R.ink, { flat: 1, occ: false });
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    const x1 = cx + Math.cos(a) * (r - 1.8), y1 = cy + Math.sin(a) * (r - 1.8);
    const x2 = cx + Math.cos(a + 0.7) * (r - 2.5), y2 = cy + Math.sin(a + 0.7) * (r - 2.5);
    t.poly([[cx, cy], [x1, y1], [x2, y2]], blades, { tone: i % 2 });
  }
}

const fanbit: Paint = (t, back) => {
  for (const x of [11, 21]) {
    t.cap(x, 23, x, 27.5, 1.3, R.chrome, { shade: 1, spec: false });
    t.ell(x, 28.3, 2, 1, R.ink, { flat: 1, spec: false });
  }
  // Heat vents: little flames off the top.
  for (const [x, h] of [[10, 4], [16, 6], [22, 4]] as const) {
    t.poly([[x - 1.6, 8], [x + 1.6, 8], [x, 8 - h]], R.ember, { grad: true });
  }
  if (back) {
    t.slab(16, 17, 9, 9, 2.5, R.chrome);
    for (let y = 11; y <= 23; y += 2) t.hline(10, y, 13, R.ink, 1);
    return;
  }
  t.slab(16, 17, 9, 9, 2.5, R.chrome);
  fanDisc(t, 16, 17, 7.5, R.ember);
  // Face on the hub.
  t.ell(16, 17, 3.6, 3.6, R.steel, { spec: false });
  eyeDot(t, 14, 16); eyeDot(t, 17, 16);
  t.hline(15, 19, 2, INK, 1);
  for (const [x, y] of [[8, 9], [24, 9], [8, 25], [24, 25]] as const) rivet(t, x, y, R.steel);
};

const fanstorm: Paint = (t, back) => {
  // Heat-sink fins fan out like wings.
  for (let i = 0; i < 4; i++) {
    t.slab(3 + i * 1.2, 9 + i * 4, 3, 1, 0.6, R.chrome, { shade: i % 2 });
    t.slab(29 - i * 1.2, 9 + i * 4, 3, 1, 0.6, R.chrome, { shade: i % 2 });
  }
  for (const x of [11, 21]) t.slab(x, 27.5, 2.6, 1.6, 0.8, R.ink, { flat: 1 });
  t.slab(16, 16, 10, 12.5, 2, R.chrome);
  // RGB strip down the side.
  for (let y = 5; y < 27; y += 2) t.px(6, y, [R.neon, R.aqua, R.lime][(y >> 1) % 3]!, 1);
  if (back) {
    for (let y = 6; y < 27; y += 3) t.hline(9, y, 14, R.ink, 1);
    return;
  }
  fanDisc(t, 16, 9, 5.2, R.ember);
  fanDisc(t, 16, 21, 5.2, R.lava);
  // Face across the middle band.
  visor(t, 9, 14, 14);
  t.px(12, 15, R.ember, 0); t.px(19, 15, R.ember, 0);
  for (const x of [11, 16, 21]) t.poly([[x - 1.2, 3.5], [x + 1.2, 3.5], [x, 0]], R.ember, { grad: true });
};

/** Moth wing: rounded quad with circuit traces or LED grid. */
function mothWing(t: Toy, cx: number, cy: number, rx: number, ry: number, side: 1 | -1, ramp: Ramp): void {
  t.ell(cx, cy, rx, ry, ramp, { dome: 0.35, spec: false });
  t.ring(cx, cy, rx - 0.8, ry - 0.8, ramp, 3);
  void side;
}

const ledmoth: Paint = (t, back) => {
  mothWing(t, 7.5, 12, 6.5, 6, -1, R.green);
  mothWing(t, 24.5, 12, 6.5, 6, 1, R.green);
  mothWing(t, 9, 21, 4.5, 4, -1, R.moss);
  mothWing(t, 23, 21, 4.5, 4, 1, R.moss);
  if (!back) {
    trace(t, 4, 10, 8, 14); trace(t, 27, 10, 23, 14); trace(t, 6, 20, 10, 23); trace(t, 26, 20, 22, 23);
    for (const [x, y] of [[5, 13], [26, 13], [9, 8], [22, 8]] as const) t.px(x, y, R.aqua, 0);
  }
  // Fuzzy body.
  t.ell(16, 18, 4, 8, R.steel);
  for (let y = 14; y <= 23; y += 3) t.hline(13, y, 7, R.chrome, 1);
  t.ell(16, 9, 4.5, 4, R.steel);
  // LED antennae.
  t.line(14, 6, 11, 1, R.chrome, 2);
  t.line(18, 6, 21, 1, R.chrome, 2);
  t.ell(11, 1, 1.4, 1.4, R.aqua, { spec: false, rim: false });
  t.ell(21, 1, 1.4, 1.4, R.aqua, { spec: false, rim: false });
  t.px(11, 0, WHITE); t.px(21, 0, WHITE);
  for (const x of [14, 18]) t.vline(x, 26, 3, R.chrome, 2);
  if (back) return;
  eyeBig(t, 12, 7, R.aqua);
  eyeBig(t, 17, 7, R.aqua);
};

const rackmoth: Paint = (t, back) => {
  mothWing(t, 6, 11, 6.5, 8, -1, R.chrome);
  mothWing(t, 26, 11, 6.5, 8, 1, R.chrome);
  mothWing(t, 8, 22, 5, 5, -1, R.navy);
  mothWing(t, 24, 22, 5, 5, 1, R.navy);
  if (!back) {
    // Rows of status LEDs on the upper wings.
    for (let r = 0; r < 4; r++) for (let c = 0; c < 3; c++) {
      const on = (r * 3 + c) % 4;
      t.px(3 + c * 2, 7 + r * 2, on === 0 ? R.lime : on === 1 ? R.aqua : R.slate, 1);
      t.px(25 + c * 2, 7 + r * 2, on === 2 ? R.lime : on === 3 ? R.neon : R.slate, 1);
    }
  }
  // The body is a slim server rack.
  t.slab(16, 17, 4.5, 9, 1, R.ink, { shade: -1 });
  for (let y = 10; y <= 24; y += 2) {
    t.hline(13, y, 7, R.chrome, 2);
    t.px(18, y, y % 4 ? R.lime : R.aqua, 1);
  }
  t.ell(16, 6.5, 5, 4, R.chrome);
  t.line(13, 3, 9, -1, R.chrome, 2);
  t.line(19, 3, 23, -1, R.chrome, 2);
  t.px(9, 0, R.aqua, 0); t.px(23, 0, R.aqua, 0);
  for (const x of [13, 19]) t.vline(x, 26, 3, R.chrome, 2);
  if (back) return;
  eyeGlow(t, 12, 5, R.aqua);
  eyeGlow(t, 17, 5, R.aqua);
};

const neuralith: Paint = (t, back) => {
  // Crescent halo behind.
  t.ring(16, 12, 13, 11, R.moon, 1);
  t.ring(16, 12, 12, 10, R.moon, 2);
  crescent(t, 27, 5, 3.4, R.moon, R.night);
  // Floating monolith.
  t.ell(16, 29, 6, 1, R.aqua, { flat: 2, occ: false });
  t.poly([[10, 26], [9, 8], [16, 1], [23, 8], [22, 26]], R.chrome, { grad: true });
  t.poly([[16, 1], [16, 26], [10, 26], [9, 8]], R.steel, { tone: 1 });
  // Neural pattern: glowing traces between nodes.
  const nodes: [number, number][] = [[12, 10], [19, 9], [14, 16], [20, 17], [12, 22], [18, 23]];
  for (let i = 0; i + 1 < nodes.length; i++) t.line(nodes[i]![0], nodes[i]![1], nodes[i + 1]![0], nodes[i + 1]![1], R.aqua, 1);
  for (const [x, y] of nodes) { t.px(x, y, R.aqua, 0); t.px(x + 1, y, WHITE); }
  if (back) return;
  // One great eye.
  t.ell(16, 13, 4.5, 3.4, R.paper, { spec: false, occ: false });
  t.ell(16, 13, 2.2, 2.4, R.night, { flat: 1, occ: false });
  t.px(15, 12, WHITE);
  t.hline(12, 9, 9, R.ink, 1);
};

// ------------------------------------------------------------------------------------------------
// Mempool Caverns
// ------------------------------------------------------------------------------------------------

/** Bat wing: a fan of three scallops from the shoulder. */
function batWing(t: Toy, sx: number, sy: number, dir: 1 | -1, span: number, ramp: Ramp): void {
  const tip = sx + dir * span;
  t.poly([[sx, sy - 2], [tip, sy - 7], [tip - dir * 1, sy + 3], [sx + dir * span * 0.66, sy + 1], [sx + dir * span * 0.4, sy + 4], [sx, sy + 3]], ramp, { grad: true });
  t.line(sx, sy - 1, tip, sy - 7, ramp, 3);
  t.line(sx, sy, sx + dir * span * 0.66, sy + 1, ramp, 3);
}

const pendbat: Paint = (t, back) => {
  batWing(t, 11, 15, -1, 10, R.plum);
  batWing(t, 21, 15, 1, 10, R.plum);
  for (const x of [13, 19]) t.cap(x, 22, x, 27.5, 1, R.ink, { spec: false, flat: 0 });
  t.ell(16, 17, 6.5, 7, R.violet);
  // Big ears.
  t.poly([[10, 12], [8, 1], [14, 8]], R.violet, { grad: true });
  t.poly([[22, 12], [24, 1], [18, 8]], R.violet, { grad: true });
  if (back) return;
  t.poly([[10.5, 10], [9, 3.5], [12.5, 7.5]], R.plum, { tone: 0 });
  t.poly([[21.5, 10], [23, 3.5], [19.5, 7.5]], R.plum, { tone: 0 });
  t.ell(16, 19.5, 3.6, 3.2, R.plum, { dome: 0.4, rim: false, spec: false });
  // It hugs an hourglass: pending.
  t.poly([[14, 17], [18, 17], [16, 20], [18, 23], [14, 23], [16, 20]], R.sand, { tone: 1 });
  t.hline(14, 17, 5, R.bark, 1); t.hline(14, 23, 5, R.bark, 1);
  eyeSleepy(t, 11, 12);
  eyeSleepy(t, 18, 12);
  t.px(15, 15, WHITE); t.px(17, 15, WHITE);
};

const stuckwing: Paint = (t, back) => {
  batWing(t, 10, 14, -1, 10, R.plum);
  batWing(t, 22, 14, 1, 10, R.plum);
  // Unconfirmed transactions stuck to the wings.
  for (const [x, y] of [[3, 9], [5, 14], [26, 8], [27, 13], [7, 5]] as const) {
    t.rect(x, y, 3, 2, back ? R.paper : R.cream, back ? 2 : 1);
    t.px(x + 1, y, R.red, 1);
  }
  for (const x of [12, 20]) t.cap(x, 22, x, 27.5, 1.2, R.ink, { spec: false, flat: 0 });
  t.ell(16, 16.5, 8, 8, R.violet);
  t.poly([[9, 11], [6, -0.5], [14, 6]], R.violet, { grad: true });
  t.poly([[23, 11], [26, -0.5], [18, 6]], R.violet, { grad: true });
  if (back) return;
  t.ell(16, 19, 5, 4.5, R.plum, { dome: 0.4, rim: false, spec: false });
  eyeGlow(t, 10, 11, R.red);
  eyeGlow(t, 18, 11, R.red);
  brow(t, 10, 11, 1);
  brow(t, 18, 11, -1);
  t.hline(12, 17, 8, INK, 1);
  t.px(13, 18, WHITE); t.px(18, 18, WHITE);
};

const shroomint: Paint = (t, back) => {
  for (const x of [12, 20]) t.ell(x, 27.8, 2.4, 1.6, R.paper, { shade: 1, spec: false });
  t.slab(16, 21, 6, 6, 3, R.paper);
  shroomCap(t, 16, 11, 11.5, 7.5, R.lime, R.aqua);
  // A coin stamp on the cap.
  if (!back) {
    t.ring(16, 8, 2.4, 2, R.gold, 1);
    t.px(16, 8, R.gold, 0);
  }
  t.hline(7, 17, 18, R.moss, 3);
  if (back) return;
  eyeSleepy(t, 11, 20);
  eyeSleepy(t, 18, 20);
  smile(t, 14, 24, 4);
  t.px(10, 23, R.rose, 1); t.px(22, 23, R.rose, 1);
};

const shroomancer: Paint = (t, back) => {
  // Staff with a small glowing mushroom.
  t.cap(27, 28, 26, 10, 0.9, R.bark, { spec: false });
  shroomCap(t, 26, 9, 3.4, 2.4, R.aqua, R.lime);
  // Robe.
  t.poly([[8, 29], [11, 17], [21, 17], [24, 29]], R.moss, { grad: true });
  t.hline(8, 28, 17, R.moss, 3);
  // Face tucked under a wide glowing cap.
  t.ell(16, 16.5, 5.5, 4, R.paper, { spec: false });
  shroomCap(t, 16, 8, 12.5, 6.5, R.lime, R.aqua);
  t.hline(5, 14, 23, R.moss, 3);
  if (!back) {
    eyeGlow(t, 12, 15, R.lime);
    eyeGlow(t, 17, 15, R.lime);
    // Mycelium beard and the arm reaching for the staff.
    for (let i = 0; i < 5; i++) t.vline(12 + i * 2, 19, 3 + (i % 3), R.paper, 1 + (i % 2));
    t.cap(20, 21, 25.5, 20, 1.4, R.moss, { shade: 1 });
    t.ell(26, 20, 1.6, 1.6, R.paper, { spec: false });
  }
  // Floating spores.
  for (const [x, y] of [[3, 17], [5, 23], [29, 15], [2, 21]] as const) { t.px(x, y, R.lime, 0); t.px(x + 1, y, R.aqua, 1); }
  if (back) t.hline(10, 19, 12, R.moss, 3);
};

const hashgrub: Paint = (t, back) => {
  // Segmented larva: each segment a little chip with gold pins.
  const segs: [number, number, number][] = back
    ? [[8, 24, 4.4], [14, 24.5, 5], [20, 23, 5.2], [24, 18, 5]]
    : [[24, 24, 4.4], [18, 24.5, 5], [12, 23, 5.2], [8, 18, 5]];
  for (const [x, y, r] of segs) {
    t.slab(x, y, r, r * 0.8, r * 0.5, R.steel);
    for (let k = -1; k <= 1; k++) { t.px(Math.round(x + k * 2), Math.round(y + r * 0.8), R.gold, 1); t.px(Math.round(x + k * 2), Math.round(y - r * 0.8), R.gold, 1); }
  }
  const [hx, hy] = back ? [24, 13] : [8, 13];
  t.ell(hx, hy, 6.5, 6, R.ice);
  // Mandibles.
  t.poly([[hx - 4, hy + 4], [hx - 6, hy + 7], [hx - 2, hy + 5]], R.chrome, { tone: 1 });
  t.poly([[hx + 4, hy + 4], [hx + 6, hy + 7], [hx + 2, hy + 5]], R.chrome, { tone: 1 });
  if (back) return;
  eyeBig(t, 4, 10, R.blue);
  eyeBig(t, 9, 10, R.blue);
  smile(t, 6, 16, 4);
};

const asicarab: Paint = (t, back) => {
  // Six legs.
  for (const [x0, y0, x1, y1] of [[9, 20, 3, 25], [9, 16, 2, 17], [10, 24, 6, 29], [23, 20, 29, 25], [23, 16, 30, 17], [22, 24, 26, 29]] as const) {
    t.cap(x0, y0, x1, y1, 1, R.ink, { spec: false, flat: 0 });
  }
  // Head with twin horns.
  t.ell(16, 7.5, 5.5, 4, R.chrome);
  t.poly([[13, 5], [11, -0.5], [15, 3]], R.ice, { grad: true });
  t.poly([[19, 5], [21, -0.5], [17, 3]], R.ice, { grad: true });
  // Shell: a square mining chip.
  t.slab(16, 19, 10, 9, 2, R.ice);
  t.slab(16, 19, 6.5, 6, 1, R.ink, { flat: 1 });
  for (let i = 0; i < 5; i++) {
    t.px(11 + i * 2.5, 9.5, R.gold, 1);
    t.px(11 + i * 2.5, 28.3, R.gold, 1);
  }
  if (back) {
    t.vline(16, 11, 16, R.ice, 3);
    return;
  }
  trace(t, 12, 16, 15, 20);
  trace(t, 20, 16, 17, 21);
  t.rect(14, 18, 4, 4, R.aqua, 1);
  t.px(14, 18, WHITE);
  eyeGlow(t, 11, 5, R.aqua);
  eyeGlow(t, 18, 5, R.aqua);
};

const geodeggo: Paint = (t, back) => {
  for (const x of [12, 20]) t.ell(x, 28, 2.2, 1.4, R.stone, { shade: 2, spec: false });
  t.ell(16, 17, 10, 11.5, R.stone);
  // Rough rock speckle.
  for (const [x, y] of [[10, 9], [21, 11], [8, 20], [23, 22], [15, 26], [19, 6]] as const) t.px(x, y, R.stone, 3);
  if (back) {
    t.line(9, 14, 13, 18, R.stone, 4);
    return;
  }
  // The crack, with crystals glinting inside and eyes peeking out.
  t.poly([[7, 15], [11, 12], [14, 15], [18, 11], [22, 15], [25, 13], [25, 19], [21, 17], [17, 20], [13, 17], [7, 20]], R.ink, { tone: 1 });
  for (const [x, y] of [[9, 17], [12, 16], [21, 16], [24, 16]] as const) { t.px(x, y, R.crystal, 1); t.px(x, y - 1, R.crystal, 0); }
  eyeDot(t, 14, 16); eyeDot(t, 18, 15);
};

const geodrake: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Rock tail with a crystal tip.
  t.cap(m(20), 23, m(28), 21, 2.4, R.stone, { shade: 1 });
  blade(t, m(28), 21, m(31), 15, 1.5, R.crystal);
  // Stubby wings.
  t.poly([[m(9), 14], [m(2), 8], [m(4), 17]], R.stone, { grad: true });
  t.poly([[m(22), 14], [m(29), 8], [m(27), 17]], R.stone, { grad: true });
  t.ell(15, 21, 9, 7, R.stone);
  for (const x of [9, 20]) t.slab(x, 27, 2.8, 2, 1, R.stone, { shade: 2 });
  t.ell(15, 11, 7, 6, R.stone);
  // Crystal spikes along the back and crown, drawn over the silhouette so they read.
  for (const [x, y, h] of [[6, 17, 7], [23, 16, 8], [10, 7, 6], [20, 7, 6], [15, 6, 8]] as const) {
    blade(t, m(x), y, m(x) + (back ? 1 : -1) * (x < 15 ? -1 : x > 15 ? 1 : 0), y - h, 1.9, R.crystal);
  }
  if (back) return;
  t.ell(15, 14, 4.2, 2.6, R.stone, { shade: 1, dome: 0.4, rim: false, spec: false });
  nose(t, 14, 13);
  teeth(t, 12, 15, 6);
  eyeGlow(t, 9, 8, R.violet);
  eyeGlow(t, 17, 8, R.violet);
  brow(t, 9, 8, 1);
  brow(t, 17, 8, -1);
};

// ------------------------------------------------------------------------------------------------
// Coral Ledger
// ------------------------------------------------------------------------------------------------

const shrimpling: Paint = (t, back) => {
  // Curled shrimp body: overlapping segments from tail (bottom right) to head (top left).
  const segs: [number, number, number][] = [[22, 26, 3.2], [24, 21, 3.8], [22, 16, 4.4], [17, 13, 5], [12, 15, 5.2]];
  for (const [x, y, r] of back ? segs.map(([x, y, r]) => [31 - x, y, r] as [number, number, number]) : segs) t.ell(x, y, r, r * 0.85, R.coral);
  t.poly(back ? [[11, 27], [5, 29], [8, 24]] : [[21, 27], [27, 29], [24, 24]], R.coral, { tone: 2 });
  // Legs.
  for (const x of back ? [14, 17, 20] : [11, 14, 17]) t.cap(x, 19, x - 1, 26, 0.7, R.coral, { shade: 2, spec: false });
  if (back) {
    for (const [x, y] of [[9, 16], [15, 13]] as const) t.hline(x, y, 4, R.coral, 3);
    return;
  }
  // Long antennae.
  t.line(9, 11, 2, 2, R.coral, 2);
  t.line(12, 10, 8, 0, R.coral, 2);
  stalkEye(t, 9, 13, 8, 9, R.coral);
  stalkEye(t, 14, 12, 15, 8, R.coral);
  smile(t, 9, 17, 3);
};

const krillionaire: Paint = (t, back) => {
  const segs: [number, number, number][] = [[23, 26, 3.8], [25, 20, 4.4], [22, 14, 5.2], [16, 13, 6], [11, 17, 6]];
  for (const [x, y, r] of back ? segs.map(([x, y, r]) => [31 - x, y, r] as [number, number, number]) : segs) t.ell(x, y, r, r * 0.85, R.coral);
  // Gold chain across the shell.
  t.line(back ? 26 : 6, 16, back ? 14 : 18, 20, R.gold, 1);
  for (let i = 0; i < 5; i++) t.px((back ? 26 : 6) + (back ? -1 : 1) * i * 2.5, 16 + i, R.gold, 0);
  // Claws holding up coins.
  claw(t, back ? 21 : 10, 21, back ? 28 : 3, 23, 3, R.coral);
  t.ell(back ? 28 : 3, 27.5, 2, 1.2, R.gold, { spec: false });
  if (back) return;
  // Top hat.
  t.slab(11, 6, 4, 3.5, 0.6, R.ink, { shade: -1 });
  t.hline(6, 9, 11, R.ink, 1);
  t.hline(7, 8, 9, R.red, 1);
  stalkEye(t, 8, 14, 6, 11, R.coral);
  stalkEye(t, 14, 13, 15, 10, R.coral);
  // Monocle.
  t.ring(15, 9, 2, 2, R.gold, 1);
  t.line(17, 10, 18, 14, R.gold, 2);
  grin(t, 8, 18, 5, 2);
  t.line(4, 12, 0, 4, R.coral, 2);
};

const sidecrab: Paint = (t, back) => {
  for (const s of [-1, 1]) for (const k of [0, 1, 2]) t.cap(16 + s * 6, 22 + k, 16 + s * (10 + k), 26 + k, 0.9, R.coral, { shade: 2, spec: false });
  t.ell(16, 20, 9.5, 6.5, R.coral);
  claw(t, 9, 18, 4, 11, 3.4, R.coral);
  claw(t, 23, 18, 28, 11, 3.4, R.coral);
  if (back) {
    // The sideways chart, flat as a pancake, painted on the shell.
    t.hline(9, 19, 14, R.paper, 1);
    for (let x = 9; x < 23; x += 3) t.px(x, 18 + ((x >> 1) % 2), R.paper, 1);
    return;
  }
  stalkEye(t, 13, 15, 12, 10, R.coral);
  stalkEye(t, 19, 15, 20, 10, R.coral);
  t.hline(12, 21, 8, R.paper, 1);
  for (const x of [13, 16, 19]) t.px(x, 20, R.paper, 1);
  smile(t, 14, 23, 4);
};

const crabstack: Paint = (t, back) => {
  for (const s of [-1, 1]) for (const k of [0, 1, 2]) t.cap(16 + s * 7, 24 + k * 0.8, 16 + s * (11 + k), 28, 1.1, R.coral, { shade: 2, spec: false });
  t.ell(16, 22, 10.5, 6.5, R.coral);
  // A stack of coins balanced on the shell, crowned by a small crab.
  for (let i = 0; i < 4; i++) t.ell(16, 16 - i * 2.4, 5.5 - i * 0.4, 1.6, R.gold, { spec: false });
  t.ell(16, 5.5, 3.5, 2.4, R.coral);
  t.px(15, 4, INK, 1); t.px(17, 4, INK, 1);
  claw(t, 7, 20, 2, 12, 4.4, R.coral);
  claw(t, 25, 20, 30, 12, 4.4, R.coral);
  if (back) return;
  stalkEye(t, 12, 19, 10, 15, R.coral);
  stalkEye(t, 20, 19, 22, 15, R.coral);
  teeth(t, 12, 24, 8);
  brow(t, 9, 15, 1);
  brow(t, 21, 15, -1);
};

const jellyfud: Paint = (t, back) => {
  // Tentacles with FUD sparks.
  for (let i = 0; i < 6; i++) tentacle(t, 9 + i * 2.8, 17, 10 + (i % 3) * 2, i, R.aqua);
  for (const [x, y] of [[8, 25], [18, 28], [24, 24]] as const) t.px(x, y, R.neon, 0);
  // Translucent bell.
  t.ell(16, 12, 10, 8.5, R.aqua, { dome: 0.6 });
  t.ell(16, 16, 9.5, 3, R.plum, { dome: 0.3, spec: false, rim: false });
  t.hline(7, 18, 18, R.plum, 2);
  if (back) {
    t.ring(16, 11, 5, 4, R.aqua, 3);
    return;
  }
  eyeBig(t, 10, 9, R.plum);
  eyeBig(t, 18, 9, R.plum);
  // Nervous wobbly mouth.
  t.px(14, 15, INK, 1); t.px(15, 14, INK, 1); t.px(16, 15, INK, 1); t.px(17, 14, INK, 1);
  t.px(8, 13, R.rose, 1); t.px(24, 13, R.rose, 1);
};

const medusell: Paint = (t, back) => {
  for (let i = 0; i < 8; i++) tentacle(t, 5 + i * 3, 17, 11 + (i % 3) * 2, i * 1.3, i % 2 ? R.plum : R.aqua);
  t.ell(16, 11, 12.5, 9.5, R.plum, { dome: 0.6 });
  t.ell(16, 15.5, 12, 3.5, R.aqua, { dome: 0.3, spec: false, rim: false });
  if (!back) {
    // A red sell candle pattern.
    for (const [x, h] of [[8, 5], [12, 7], [20, 6], [24, 4]] as const) {
      t.rect(x, 5, 2, h, R.red, 1);
      t.vline(x, 4, h + 2, R.red, 2);
    }
    t.poly([[14, 6], [18, 6], [16, 10]], R.red, { tone: 0 });
  }
  crown(t, 16, 1, 8);
  if (back) return;
  eyeGlow(t, 10, 10, R.aqua);
  eyeGlow(t, 18, 10, R.aqua);
  brow(t, 10, 10, 1);
  brow(t, 18, 10, -1);
  frown(t, 14, 14, 5);
};

const pearlclam: Paint = (t, back) => {
  // Bottom shell.
  t.ell(16, 23, 12, 5.5, R.coral, { dome: 0.5 });
  for (let i = -4; i <= 4; i++) t.line(16, 26, 16 + i * 2.6, 19, R.coral, 3);
  if (back) {
    t.ell(16, 16, 12, 8, R.coral, { dome: 0.5 });
    for (let i = -4; i <= 4; i++) t.line(16, 22, 16 + i * 2.6, 9, R.coral, 3);
    return;
  }
  // Top shell tipped open.
  t.ell(16, 9, 11.5, 5, R.coral, { dome: 0.5 });
  for (let i = -4; i <= 4; i++) t.line(16, 6, 16 + i * 2.4, 13, R.coral, 3);
  t.ell(16, 16, 10, 4, R.ink, { flat: 1, occ: false });
  // The moon-pearl.
  t.ell(16, 16.5, 4, 4, R.pearl);
  crescent(t, 18, 15, 1.3, R.moon, R.pearl);
  // Eyes along the lip.
  eyeDot(t, 8, 18); eyeDot(t, 22, 18);
  t.hline(10, 21, 12, R.coral, 3);
};

// ------------------------------------------------------------------------------------------------
// Cloud Chain (MOON)
// ------------------------------------------------------------------------------------------------

const lunapup: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(m(21), 22, m(26), 16, 1.7, R.night, { shade: 1 });
  star(t, m(27), 14);
  t.ell(15, 22.5, 7.5, 6, R.night);
  if (!back) t.ell(15, 24, 4.5, 3.8, R.pearl, { dome: 0.5, rim: false, spec: false });
  paws(t, 15, 4.5, 2.6, R.pearl);
  t.ell(15, 12, 7.5, 6.5, R.night);
  t.poly([[9, 9], [7, 1], [12, 5]], R.night, { grad: true });
  t.poly([[21, 9], [23, 1], [18, 5]], R.night, { grad: true });
  if (back) {
    crescent(t, 15, 10, 2.2, R.moon, R.night);
    return;
  }
  crescent(t, 15, 7.5, 2, R.moon, R.night);
  t.ell(15, 15, 4.5, 3, R.pearl, { dome: 0.5, rim: false, spec: false });
  nose(t, 14, 14);
  smile(t, 13, 16, 4);
  eyeBig(t, 9, 9, R.moon);
  eyeBig(t, 17, 9, R.moon);
};

const lunawolf: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Aurora mane tail.
  t.cap(m(21), 19, m(29), 10, 2.4, R.night);
  for (const [x, y] of [[29, 8], [27, 12], [30, 13]] as const) star(t, m(x), y);
  t.ell(15, 20, 9.5, 7.5, R.night);
  for (const x of [8, 21]) {
    t.cap(x, 22, x, 27.5, 2.6, R.night, { shade: 1, spec: false });
    t.ell(x, 28.2, 2.8, 1.3, R.pearl, { spec: false });
  }
  if (!back) crescent(t, 15, 21, 4, R.moon, R.night);
  // Aurora mane.
  for (const [x, h, ramp] of [[7, 6, R.aqua], [11, 8, R.violet], [16, 9, R.aqua], [21, 7, R.violet]] as const) {
    t.poly([[x - 2.4, 17], [x + 2.4, 17], [x, 17 - h]], ramp, { grad: true });
  }
  t.ell(15, 10, 7.5, 6.5, R.night);
  // Head tilted up, howling.
  t.poly([[9, 7], [7, -0.5], [13, 4]], R.night, { grad: true });
  t.poly([[21, 7], [23, -0.5], [17, 4]], R.night, { grad: true });
  if (back) return;
  t.ell(15, 13, 4.5, 3.4, R.pearl, { dome: 0.5, rim: false, spec: false });
  nose(t, 14, 11);
  t.ell(15, 15, 2, 1.6, R.ink, { flat: 1, occ: false });
  eyeGlow(t, 8, 7, R.moon);
  eyeGlow(t, 18, 7, R.moon);
};

const rocketot: Paint = (t, back) => {
  // Exhaust puff at its feet.
  cloudPuff(t, 16, 28.5, 10, 2.4, R.cloud);
  fin(t, 10, 23, -1, R.red);
  fin(t, 22, 23, 1, R.red);
  t.ell(16, 16, 6.5, 11, R.paper);
  // Nose cone.
  t.poly([[10.5, 9], [21.5, 9], [16, 1]], R.red, { grad: true });
  t.hline(10, 22, 13, R.red, 1);
  if (back) {
    t.vline(16, 11, 10, R.paper, 2);
    rivet(t, 14, 13, R.steel); rivet(t, 18, 13, R.steel);
    return;
  }
  // Porthole face.
  hoop(t, 16, 14, 4.5, 4.5, 1.1, R.steel);
  t.ell(16, 14, 3.4, 3.4, R.night, { flat: 1, occ: false });
  eyeDot(t, 14, 13); eyeDot(t, 17, 13);
  t.px(16, 16, R.moon, 1);
  star(t, 15, 18, R.moon);
  t.px(9, 18, R.rose, 1); t.px(22, 18, R.rose, 1);
};

const rocketitan: Paint = (t, back) => {
  // Boosters flaming under both feet.
  for (const x of [10, 22]) {
    t.poly([[x - 2.2, 26], [x + 2.2, 26], [x, 31]], R.ember, { grad: true });
    t.slab(x, 24, 3, 2.5, 1, R.steel, { shade: 1 });
  }
  // Arms.
  t.cap(7, 12, 3, 20, 2.2, R.steel, { shade: 1 });
  t.cap(25, 12, 29, 20, 2.2, R.steel, { shade: 1 });
  t.ell(3, 21, 2.4, 2.4, R.gold); t.ell(29, 21, 2.4, 2.4, R.gold);
  t.slab(16, 15, 8.5, 10, 3.5, R.paper);
  t.poly([[8, 7], [24, 7], [16, -0.5]], R.red, { grad: true });
  t.hline(8, 7, 17, R.gold, 1);
  t.hline(8, 23, 17, R.gold, 1);
  if (back) {
    for (const x of [12, 20]) t.slab(x, 15, 2.4, 6, 1, R.steel, { shade: 1 });
    return;
  }
  visor(t, 10, 10, 12);
  t.px(12, 11, R.moon, 0); t.px(19, 11, R.moon, 0);
  crescent(t, 16, 18, 3.2, R.moon, R.paper);
  for (const [x, y] of [[11, 20], [21, 20], [11, 15], [21, 15]] as const) rivet(t, x, y, R.gold);
};

const nimbaa: Paint = (t, back) => {
  for (const x of [11, 21]) t.cap(x, 23, x, 28, 1.3, R.ink, { spec: false, flat: 0 });
  cloudPuff(t, 16, 18, 20, 12);
  // Tiny star caught in the wool.
  star(t, back ? 11 : 22, 13);
  if (back) {
    t.ell(16, 12, 3, 2.4, R.cloud, { shade: 1, spec: false });
    return;
  }
  // Dark little face with curled horns.
  t.ell(9, 17, 4.6, 4.2, R.slate, { shade: 1 });
  t.ring(5, 14, 2, 2, R.moon, 2);
  t.ring(13, 13, 2, 2, R.moon, 2);
  eyeSleepy(t, 6, 16);
  t.px(10, 16, INK, 1); t.px(11, 16, INK, 1);
  smile(t, 8, 19, 3);
};

const nimbram: Paint = (t, back) => {
  for (const x of [10, 22]) t.cap(x, 22, x, 28, 1.8, R.ink, { spec: false, flat: 0 });
  cloudPuff(t, 16, 17, 24, 14, R.slate);
  // Frost lightning in the storm wool.
  t.line(18, 9, 16, 14, R.ice, 1);
  t.line(16, 14, 19, 16, R.ice, 1);
  t.line(19, 16, 17, 21, R.ice, 0);
  if (back) {
    for (const [x, y] of [[8, 12], [24, 14]] as const) star(t, x, y, R.ice);
    return;
  }
  t.ell(8, 16, 5, 4.6, R.night, { shade: 1 });
  // Big curled ice horns.
  hoop(t, 4, 11, 3.2, 3.2, 1.3, R.ice);
  hoop(t, 13, 10, 3.2, 3.2, 1.3, R.ice);
  eyeGlow(t, 4, 15, R.ice);
  eyeGlow(t, 9, 15, R.ice);
  brow(t, 4, 15, 1);
  brow(t, 9, 15, -1);
  frown(t, 6, 19, 4);
};

const craterling: Paint = (t, back) => {
  for (const x of [12, 20]) t.ell(x, 28, 2.4, 1.4, R.moon, { shade: 2, spec: false });
  t.cap(7, 18, 4, 23, 1.6, R.moon, { shade: 1 });
  t.cap(25, 18, 28, 23, 1.6, R.moon, { shade: 1 });
  t.ell(16, 17, 10, 10.5, R.moon);
  // Craters.
  for (const [x, y, r] of [[10, 10, 2.2], [21, 9, 1.6], [23, 20, 2.4], [9, 22, 1.4], [16, 25, 1.6]] as const) {
    t.ell(x, y, r, r * 0.8, R.moon, { shade: 2, spec: false, rim: false, occ: false });
    t.px(Math.round(x - r * 0.5), Math.round(y - r * 0.5), R.moon, 4);
  }
  if (back) return;
  eyeSleepy(t, 11, 15);
  eyeSleepy(t, 18, 15);
  smile(t, 15, 19, 3);
  t.px(10, 18, R.rose, 1); t.px(22, 18, R.rose, 1);
};

const selenarch: Paint = (t, back) => {
  // Crescent-moon wings.
  crescent(t, 5, 15, 7.5, R.pearl, R.night);
  crescent(t, 27, 15, 7.5, R.pearl, R.night);
  t.ring(5, 15, 7.5, 7.5, R.moon, 2);
  t.ring(27, 15, 7.5, 7.5, R.moon, 2);
  // Talons.
  for (const x of [12, 20]) { t.hline(x - 2, 28, 5, R.moon, 2); t.px(x - 2, 29, INK, 1); t.px(x + 2, 29, INK, 1); }
  t.ell(16, 18, 8.5, 10, R.pearl);
  // Chest feathers.
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3 - (r % 2); c++) t.px(12 + c * 3 + (r % 2) * 1.5, 18 + r * 3, R.pearl, 3);
  // Ear tufts and a crown of stars.
  t.poly([[9, 11], [8, 3], [13, 8]], R.pearl, { grad: true });
  t.poly([[23, 11], [24, 3], [19, 8]], R.pearl, { grad: true });
  for (const [x, y] of [[12, 2], [16, 0.5], [20, 2]] as const) star(t, x, Math.round(y) + 1);
  if (back) {
    for (let r = 0; r < 4; r++) t.hline(11, 12 + r * 4, 10, R.pearl, 3);
    return;
  }
  // Facial disc, big lunar eyes and a small beak.
  t.ell(16, 12, 6.5, 4.8, R.cloud, { dome: 0.4, rim: false, spec: false });
  t.ell(12.5, 11.5, 2.6, 2.6, R.moon, { spec: false, occ: false });
  t.ell(19.5, 11.5, 2.6, 2.6, R.moon, { spec: false, occ: false });
  t.rect(12, 11, 2, 2, INK, 1); t.rect(19, 11, 2, 2, INK, 1);
  t.px(12, 11, WHITE); t.px(19, 11, WHITE);
  t.poly([[15, 13], [17, 13], [16, 16]], R.gold, { grad: true });
};

export const UNDERNET_PAINT = {
  chatbit, chatlord, glitchpup, glitchhound, fanbit, fanstorm, ledmoth, rackmoth, neuralith,
  pendbat, stuckwing, shroomint, shroomancer, hashgrub, asicarab, geodeggo, geodrake,
  shrimpling, krillionaire, sidecrab, crabstack, jellyfud, medusell, pearlclam,
  lunapup, lunawolf, rocketot, rocketitan, nimbaa, nimbram, craterling, selenarch,
} satisfies Record<string, Paint>;

void arc; void grin;
